/**
 * Maximum length for a subdomain (DNS label limit).
 */
const SUBDOMAIN_MAX_LENGTH = 63;

/**
 * Convert an organization name into a valid subdomain slug.
 *
 * Matches the signup form's subdomain rules: lowercase, must start with a
 * letter, and may contain only letters, numbers, and hyphens.
 *
 * @param {string} name - The organization name to convert
 * @returns {string} A valid subdomain slug, or an empty string if none can be derived
 */
export function slugifySubdomain(name) {
  if (typeof name !== "string") {
    return "";
  }

  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-") // collapse runs of invalid characters into hyphens
    .replace(/^[^a-z]+/, "") // must start with a letter
    .slice(0, SUBDOMAIN_MAX_LENGTH)
    .replace(/-+$/, "");
}

/**
 * The domain every organization lives under, e.g. "graphit.io".
 *
 * Must match the backend's TENANT_DOMAIN_BASE setting, which is what
 * TenantContextMiddleware actually resolves tenants against - hence an env var
 * rather than a literal, so the two can't drift. One definition, shared by
 * every screen that shows an organization's address.
 */
export const TENANT_DOMAIN_BASE =
  import.meta.env.VITE_TENANT_DOMAIN_BASE || "graphit.io";

/**
 * An organization's address, e.g. "firstchurch.graphit.io".
 *
 * @param {string} subdomain
 * @returns {string} The address, or "" when there is no subdomain
 */
export function tenantAddress(subdomain) {
  if (typeof subdomain !== "string" || !subdomain.trim()) {
    return "";
  }
  return `${subdomain.trim()}.${TENANT_DOMAIN_BASE}`;
}
